import React, { useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import DynamicQuantitySelect from 'dibs-dynamic-quantity-select';
import { injectIntl } from 'react-intl';
import get from 'lodash.get';
import styles from './PdpSharedQuantitySelect.scss';
const INITIAL_QUANTITY = '1';

const getSelectedSku = ({ item, selectedSkuId }) => {
    const skus = get(item, 'skus') || [];
    const skuId = selectedSkuId || item.defaultSkuId;
    return skus.find(sku => sku.serviceId === skuId);
};

const getCtaQuantity = (selectedSku, selectedQuantity) => {
    const { hasUnlimitedQuantity, quantity } = selectedSku || {};
    const isValidCtaQuantity = hasUnlimitedQuantity || Number(selectedQuantity) <= quantity;
    return isValidCtaQuantity ? selectedQuantity : INITIAL_QUANTITY;
};

const getSellerStatusFields = item => {
    const sellerStatus = get(item, 'seller.status');
    const sellerIsActive = sellerStatus === 'active';
    const sellerIsComplimentary = sellerStatus === 'nopay';
    const sellerIsInternal = sellerStatus === 'internal';

    return { sellerIsActive, sellerIsComplimentary, sellerIsInternal };
};

const PdpSharedQuantitySelectComponent = props => {
    const { children, intl, item, selectedSkuId } = props;
    const [selectedQuantity, setSelectedQuantity] = useState(INITIAL_QUANTITY);
    const [quantitySelectKey, setQuantitySelectKey] = useState(selectedSkuId);
    const selectedSku = getSelectedSku(props);
    const { quantity, quantitySelectOptions, setSize } = selectedSku || {};

    // triggered when selectedSkuId changes
    useEffect(() => {
        // reset back to the initial quantity when selectedSkuId changed
        setSelectedQuantity(INITIAL_QUANTITY);
        // re-construct the DynamicQuantitySelect by changing the key
        setQuantitySelectKey(selectedSkuId);
    }, [selectedSkuId]);

    if (quantitySelectOptions) {
        const { isDeleted, isSold } = item;
        const ctaQuantity = getCtaQuantity(selectedSku, selectedQuantity);
        const { sellerIsActive, sellerIsComplimentary, sellerIsInternal } = getSellerStatusFields(
            item
        );
        const showQuantitySelect =
            (sellerIsActive || sellerIsInternal || sellerIsComplimentary) && !isDeleted && !isSold;

        return (
            <React.Fragment>
                <div
                    className={`boxSizingWrapper ${styles.quantitySelect}`}
                    data-tn="quantity-select-wrapper"
                >
                    {showQuantitySelect && (
                        <DynamicQuantitySelect
                            key={quantitySelectKey}
                            availableQuantity={quantity}
                            hasAnimatedPlaceholder
                            intl={intl}
                            onChange={e => setSelectedQuantity(e.target.value)}
                            value={selectedQuantity}
                            options={quantitySelectOptions}
                            soldAsSet={setSize && setSize > 1}
                        />
                    )}
                </div>
                {children(ctaQuantity)}
            </React.Fragment>
        );
    } else {
        return children(null);
    }
};

PdpSharedQuantitySelectComponent.propTypes = {
    item: PropTypes.object.isRequired,
    children: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,
};

export const PdpSharedQuantitySelect = createFragmentContainer(
    injectIntl(PdpSharedQuantitySelectComponent),
    {
        item: graphql`
            fragment PdpSharedQuantitySelect_item on Item {
                defaultSkuId
                isSold
                isDeleted
                seller {
                    status
                }
                skus {
                    serviceId
                    quantity
                    hasUnlimitedQuantity
                    quantitySelectOptions {
                        label
                        value
                    }
                    setSize
                }
            }
        `,
    }
);
